import Analytics from '@/utils/analytics';
import ChainLinkIcon from '@assets/icons/chain-link.svg';
import HelpCircle from '@assets/icons/help-circle.svg';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActiveNavEntry,
  NavigationStateType,
  RouteMapType,
  useSideNavPress,
} from '@/hooks/use-side-nav-press';
import { APP_CONTAINER_ROUTES, APP_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { AuthStatus } from '@/enums';
import { Avatar } from '@/components/avatar';
import { AvatarMenuActionSheet } from './menu-action-sheet/avatar-menu-action-sheet';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { DrawerComponentProps } from '@/components/drawer';
import { DrawerItem } from '@react-navigation/drawer';
import { DrawerItemList } from './drawer-item-list';
import { DrawerLabel } from './drawer-label';
import { ExternalContentLinkIcon } from '@/components/external-content/external-content-link-icon';
import {
  ExternalContentLinkListItem,
  isExternalContentStaticLink,
  useExternalContent,
} from '@/contexts/external-content-context';
import { getMicroProductIconConfig } from '@/screens/storefront-microproducts/micro-product-icon-config';
import { IconHolder, Icons } from '@usepepper/sprout-kit-ui';
import {
  Image,
  Linking,
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { isWeb } from '@/utils/navigation';
import {
  LayoutSize,
  useLayoutContext,
  useLayoutSizeValue,
} from '@/contexts/layout-context';
import { MORE_MENU_ABSORBED_ROUTES } from './more-menu/more-menu-absorbed-routes';
import {
  MORE_MENU_ICON_SIZE,
  MoreMenuItem,
  MoreMenuSection,
} from './more-menu/more-menu-item-icon';
import { MoreMenuActionSheet } from './more-menu/more-menu-action-sheet';
import { MoreSubmenuList } from './more-menu/more-submenu-list';
import { partnerSupplier } from '@/utils/environment';
import { RedirectActionSheet } from '@/components/redirect-action-sheet';
import { SCREEN_PADDING } from '@/styles';
import { SideNavItemList } from './side-nav-item-list';
import { StackNavigationProp } from '@react-navigation/stack';
import { SvgProps } from 'react-native-svg';
import { useIntercomContext } from '@/contexts/intercom-context';
import { useMaybeUserContext } from '@/contexts/user-context';
import { useNavigation } from '@react-navigation/native';
import { useNavigationConfirmationContext } from '@/contexts/navigation-confirmation-context';
import { usePricingExperienceNavigation } from '@/hooks/use-pricing-experience-navigation';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSignOut } from '@/hooks/use-sign-out';
import { useStorefrontMicroProducts } from '@/screens/storefront-microproducts/blocs/use-storefront-microproducts';

const BOTTOM_LINKS = new Set<string>([APP_ROUTES.Account]);

// Close ✕ — the Sprout Kit (lucide) X icon in the $md (24px) holder, which sizes
// and tints it. Button computes the themed color (its tertiary text color) and
// passes it via `fill`; we feed that to the holder so the color is unchanged.
const CloseDrawerIcon: React.FC<SvgProps> = ({ fill }) => (
  <IconHolder
    // Button passes its themed tertiary color via `fill`; forward it only when
    // it's a string (IconHolder's color prop rejects undefined).
    {...(typeof fill === 'string' ? { color: fill } : {})}
    size="$md"
  >
    <Icons.X />
  </IconHolder>
);

export const DrawerContent: React.FC<
  DrawerComponentProps<NavigationStateType>
> = ({ onClose, extendedState, open }) => {
  const intercomContext = useIntercomContext();
  const signOut = useSignOut();
  const userContext = useMaybeUserContext();
  const { layoutSize } = useLayoutContext();
  const { top } = useSafeAreaInsets();
  const [styles, theme] = useStyles();

  const user =
    userContext?.authStatus === AuthStatus.Authenticated
      ? userContext.user
      : null;

  const logoUrl = theme.images.welcomeLogoUrl;
  const isLargeLayout = layoutSize === LayoutSize.LARGE;
  const isSmallLayout = layoutSize === LayoutSize.SMALL;
  // Tablet breakpoints (medium / small-tablet): the "More" tile drills into an
  // in-drawer secondary screen rather than opening a popover or bottom sheet.
  const isTablet = !isLargeLayout && !isSmallLayout;
  const oldURL = partnerSupplier.oldWebLink;
  const { navigationBlocked } = useNavigationConfirmationContext();
  const [isAvatarMenuSheetOpen, setAvatarMenuSheetOpen] = useState(false);
  const [isMoreMenuSheetOpen, setIsMoreMenuSheetOpen] = useState(false);
  // Tablet "More" opens an in-drawer secondary screen instead of the floating
  // popover (desktop) / bottom sheet (phone).
  const [isMoreSubmenuOpen, setIsMoreSubmenuOpen] = useState(false);
  const moreMenuActionSheetRef = useRef<View>(null);
  const [isRedirectSheetOpen, setIsRedirectSheetOpen] = useState(false);
  const menuSectionRef = useRef<View>(null);
  const navigation =
    useNavigation<StackNavigationProp<AppContainerStackParams>>();
  const onSideNavPress = useSideNavPress();
  const {
    externalContentLinksList,
    selectedStaticLinkURL,
    setSelectedExternalContent,
    setSelectedStaticLinkURL,
  } = useExternalContent();
  const { canNavigate: canNavigateToPricing, navigateToPricing } =
    usePricingExperienceNavigation();
  // Dynamic storefront microproduct entries. Empty by default — the
  // microproducts GA slot config is the gate, so most users see no change.
  const { moreMenuProducts, promotedProducts } = useStorefrontMicroProducts();

  // The drawer content stays mounted when the drawer closes, so any close must
  // reset the More submenu — otherwise it reopens mid-drill on next open.
  const handleClose = useCallback(() => {
    setIsMoreSubmenuOpen(false);
    onClose();
  }, [onClose]);

  // handleClose only covers button/item closes; the drawer can also be
  // dismissed by tapping the backdrop, which calls onClose directly. Reset on
  // open so every dismissal path lands back on the top-level menu next time —
  // and resetting on open (not close) avoids a content flash mid slide-out.
  useEffect(() => {
    if (open) {
      setIsMoreSubmenuOpen(false);
    }
  }, [open]);

  const onStorefrontProductPress = useCallback(
    (productId: string) => {
      if (navigationBlocked) {
        return;
      }
      Analytics.track('navigation_drawer.microproduct.press', {
        microProductId: productId,
      });

      if (isSmallLayout) {
        // Small layout has no tab entry for microproducts (the
        // Links/ExternalContentScreen split) — push the stack screen.
        navigation.navigate(APP_CONTAINER_ROUTES.StorefrontMicroProduct, {
          productId,
        });
      } else {
        // In-container rendering: jump to the hidden microproducts tab — the
        // rail stays visible and content swaps in place, exactly like the
        // external-content links flow above.
        onSideNavPress(APP_ROUTES.StorefrontMicroProduct, false, { productId });
      }
      handleClose();
    },
    [navigationBlocked, isSmallLayout, navigation, onSideNavPress, handleClose],
  );

  // Replicates LinksMenuActionSheet's handleLinkClick, plus closing the
  // drawer (the tablet drawer previously closed itself in DrawerItemList's
  // inline link handling).
  //
  // Analytics note: desktop and tablet links previously fired two different
  // events ('external_content_links_menu…' and 'navigation_drawer…'). Both
  // surfaces now open the same More popup, so they emit one event with a
  // `surface` property preserving the desktop/tablet split. The retired
  // 'navigation_drawer.external_content_link.press' event is documented in
  // the PR body.
  const onExternalContentLinkPress = useCallback(
    (link: ExternalContentLinkListItem) => {
      Analytics.track(
        'external_content_links_menu.external_content_link.press',
        {
          externalContentUUID: link.metadata?.uuid,
          surface: isLargeLayout ? 'desktop' : 'tablet',
        },
      );

      if (isExternalContentStaticLink(link.metadata!)) {
        setSelectedStaticLinkURL(link.metadata!.config!.url!);
        setIsRedirectSheetOpen(true);
      } else {
        setSelectedExternalContent(link.metadata!);
        onSideNavPress(APP_ROUTES.Links);
      }
      handleClose();
    },
    [
      isLargeLayout,
      handleClose,
      onSideNavPress,
      setSelectedExternalContent,
      setSelectedStaticLinkURL,
    ],
  );

  // One untitled section: storefront microproducts first, then external
  // content links — the More popup replaces both the direct microproduct
  // tiles and the Links tile / inline link items. Memoized: the entries
  // carry fresh icon ReactNodes + onPress closures, so without this the
  // popup's section array changes identity every render.
  const moreMenuItems: MoreMenuItem[] = useMemo(
    () => [
      ...moreMenuProducts.map((product) => ({
        // Last-resort chain-link: its paths are fill-based, so a plain fill
        // tint is safe (unlike the stroke-based link icons below).
        ...getMicroProductIconConfig(
          product,
          <ChainLinkIcon
            fill={theme.colors.primary}
            height={MORE_MENU_ICON_SIZE}
            width={MORE_MENU_ICON_SIZE}
          />,
        ),
        label: product.displayName,
        onPress: () => onStorefrontProductPress(product.id),
      })),
      ...externalContentLinksList.map((link) => ({
        // Reuse the canonical link icon (same as the account screen): it
        // picks icon_url vs the type fallback and tints stroke-only. Passing
        // a `fill` to these stroke-based SVGs would flood-fill them into a
        // solid shape, because .svgrrc maps the asset's fill color to the
        // `fill` prop.
        icon: link.metadata ? (
          <ExternalContentLinkIcon
            iconSize={MORE_MENU_ICON_SIZE}
            link={link.metadata}
          />
        ) : undefined,
        id: String(link.value),
        label: link.title,
        onPress: () => onExternalContentLinkPress(link),
      })),
    ],
    [
      moreMenuProducts,
      externalContentLinksList,
      theme.colors.primary,
      onStorefrontProductPress,
      onExternalContentLinkPress,
    ],
  );
  const hasMoreMenuItems = moreMenuItems.length > 0;
  const moreMenuSections: MoreMenuSection[] = useMemo(
    () => [{ items: moreMenuItems }],
    [moreMenuItems],
  );

  const stateRoutes = extendedState?.routes;
  const activeRoute =
    extendedState && stateRoutes ? stateRoutes[extendedState.index] : undefined;
  const activeRouteName = activeRoute?.name;

  const activeProductId =
    activeRouteName === APP_ROUTES.StorefrontMicroProduct
      ? (activeRoute?.params as { productId?: string } | undefined)?.productId
      : undefined;

  const activeNavEntry = useMemo<ActiveNavEntry>(() => {
    if (
      activeProductId !== undefined &&
      promotedProducts.some((product) => product.id === activeProductId)
    ) {
      return { productId: activeProductId, type: 'product' };
    }

    if (
      activeRouteName !== undefined &&
      MORE_MENU_ABSORBED_ROUTES.has(activeRouteName)
    ) {
      return { type: 'more' };
    }

    return { index: extendedState?.index ?? -1, type: 'route' };
  }, [
    activeProductId,
    activeRouteName,
    extendedState?.index,
    promotedProducts,
  ]);

  const [topRoutes, bottomRoutes] = useMemo(() => {
    if (!stateRoutes) {
      return [[], []];
    }

    const topLinks: RouteMapType[] = [];
    const bottomLinks: RouteMapType[] = [];
    stateRoutes.forEach((r, i) => {
      if (BOTTOM_LINKS.has(r.name)) {
        bottomLinks.push({
          index: i,
          route: r,
        });
      } else {
        topLinks.push({
          index: i,
          route: r,
        });
      }
    });

    return [topLinks, bottomLinks];
  }, [stateRoutes]);

  const drawerItemStyle = useLayoutSizeValue<StyleProp<ViewStyle>>({
    [LayoutSize.SMALL]: styles.drawerItem,
    [LayoutSize.SMALL_TABLET]: [styles.drawerItem, styles.drawerItemMedium],
    [LayoutSize.MEDIUM]: [styles.drawerItem, styles.drawerItemMedium],
    [LayoutSize.LARGE]: styles.drawerItem,
  });

  return (
    <View
      style={[
        styles.container,
        isLargeLayout
          ? styles.drawerScrollContainerLarge
          : styles.drawerScrollContainerMedium,
      ]}
    >
      {isTablet && isMoreSubmenuOpen ? (
        <MoreSubmenuList
          onBack={() => setIsMoreSubmenuOpen(false)}
          sections={moreMenuSections}
          topInset={top}
        />
      ) : (
        <>
          <ScrollView
            contentContainerStyle={[styles.drawerScrollContainer]}
            pointerEvents={navigationBlocked ? 'none' : 'auto'}
          >
            <View
              style={[
                styles.contentHeader,
                { marginTop: top },
                isLargeLayout ? null : styles.contentHeaderTablet,
              ]}
            >
              {isLargeLayout ? null : (
                <>
                  <Button
                    accessibilityLabel={i18n.t('close')}
                    containerStyle={styles.headerItem}
                    // Left-align the icon so paddingButton's 24px left padding
                    // places the holder 24px from the drawer edge (Button content
                    // centers by default, which floats the icon to the column
                    // center).
                    contentStyle={styles.closeButtonContent}
                    Icon={CloseDrawerIcon}
                    onPress={handleClose}
                    style={styles.paddingButton}
                    testID="close-drawer-button"
                    trackingData={{
                      name: 'drawer_content.close_button',
                    }}
                    type="tertiary"
                  />
                  {logoUrl ? (
                    <Image
                      alt=""
                      resizeMode="contain"
                      source={{ uri: logoUrl }}
                      style={[styles.logoImageTablet, styles.headerItem]}
                      testID="supplier-logo"
                    />
                  ) : (
                    <View style={styles.headerItem} />
                  )}
                  <View style={styles.headerItem} />
                </>
              )}
            </View>
            <View
              style={[
                styles.drawerNavItems,
                isLargeLayout ? null : styles.drawerNavItemsTablet,
                navigationBlocked ? styles.disabled : null,
              ]}
            >
              {isLargeLayout ? (
                <SideNavItemList
                  activeEntry={activeNavEntry}
                  moreMenuActionSheetRef={moreMenuActionSheetRef}
                  onClose={handleClose}
                  onStorefrontProductPress={onStorefrontProductPress}
                  promotedProducts={promotedProducts}
                  routes={topRoutes}
                  setIsMoreMenuSheetOpen={setIsMoreMenuSheetOpen}
                  showMoreMenu={hasMoreMenuItems}
                />
              ) : (
                <>
                  <DrawerItemList
                    activeIndex={extendedState?.index ?? -1}
                    itemStyle={drawerItemStyle}
                    onClose={handleClose}
                    routes={topRoutes}
                  />
                  {promotedProducts.map((product) => {
                    const focused =
                      activeNavEntry.type === 'product' &&
                      activeNavEntry.productId === product.id;
                    return (
                      <DrawerItem
                        activeBackgroundColor={theme.colors.accent}
                        focused={focused}
                        key={product.id}
                        label={() => (
                          <DrawerLabel
                            focused={focused}
                            name={product.displayName}
                          />
                        )}
                        onPress={() => onStorefrontProductPress(product.id)}
                        style={drawerItemStyle}
                      />
                    );
                  })}
                  {hasMoreMenuItems ? (
                    <View ref={moreMenuActionSheetRef}>
                      <DrawerItem
                        activeBackgroundColor={theme.colors.accent}
                        focused={activeNavEntry.type === 'more'}
                        label={() => (
                          <View style={styles.moreLabelRow}>
                            <DrawerLabel
                              focused={activeNavEntry.type === 'more'}
                              name={i18n.t('more')}
                            />
                            {/* Tablet drills into a secondary screen — signal it
                            with a trailing chevron. Phone keeps the bottom
                            sheet, so no drill-in affordance there. */}
                            {isTablet ? (
                              <IconHolder
                                color={theme.colors.secondaryText}
                                size="$sm"
                                testID="more-tile-chevron"
                              >
                                <Icons.ChevronRight />
                              </IconHolder>
                            ) : null}
                          </View>
                        )}
                        onPress={() =>
                          isTablet
                            ? setIsMoreSubmenuOpen(true)
                            : setIsMoreMenuSheetOpen(true)
                        }
                        style={drawerItemStyle}
                      />
                    </View>
                  ) : null}
                </>
              )}
            </View>
          </ScrollView>
          <View
            ref={menuSectionRef}
            style={[
              styles.bottomList,
              navigationBlocked ? styles.disabled : null,
            ]}
          >
            {isLargeLayout && user ? (
              <>
                {intercomContext.initialized ? (
                  <TouchableOpacity
                    onPress={() => {
                      Analytics.track('navigation_drawer.support_button');
                      intercomContext.toggle();
                    }}
                    style={styles.supportButton}
                  >
                    <HelpCircle stroke={theme.colors.accentText} />
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity
                  disabled={navigationBlocked}
                  onPress={() => setAvatarMenuSheetOpen(true)}
                  style={styles.avatarContainer}
                  testID="side-nav-menu"
                >
                  <Avatar name={user.name} size="small" uuid={user.uuid} />
                </TouchableOpacity>
              </>
            ) : (
              <>
                {canNavigateToPricing ? (
                  <DrawerItem
                    label={() => (
                      <DrawerLabel
                        focused={false}
                        name={i18n.t('pricing.navigation.label')}
                      />
                    )}
                    onPress={async () => {
                      if (navigationBlocked) {
                        return;
                      }

                      await navigateToPricing();
                    }}
                    style={drawerItemStyle}
                  />
                ) : null}
                {oldURL && isWeb ? (
                  <DrawerItem
                    label={() => (
                      <DrawerLabel
                        focused={false}
                        name={i18n.t('switch_to_old_version')}
                      />
                    )}
                    onPress={() => {
                      if (navigationBlocked) {
                        return;
                      }
                      Analytics.track('old_url_link.press', {
                        location: 'navigation_drawer',
                      });
                      Linking.openURL(oldURL);
                    }}
                    style={drawerItemStyle}
                  />
                ) : null}
                <DrawerItemList
                  activeIndex={extendedState?.index ?? -1}
                  itemStyle={drawerItemStyle}
                  onClose={handleClose}
                  routes={bottomRoutes}
                />
                {intercomContext.initialized ? (
                  <DrawerItem
                    label={() => (
                      <DrawerLabel focused={false} name={i18n.t('support')} />
                    )}
                    onPress={() => {
                      Analytics.track('navigation_drawer.support_button');
                      intercomContext.toggle();
                    }}
                    style={drawerItemStyle}
                  />
                ) : null}
                <DrawerItem
                  label={() => (
                    <DrawerLabel focused={false} name={i18n.t('log_out')} />
                  )}
                  onPress={async () => {
                    if (navigationBlocked) {
                      return;
                    }

                    await signOut();
                  }}
                  style={drawerItemStyle}
                />
              </>
            )}
          </View>
        </>
      )}
      {isLargeLayout && isAvatarMenuSheetOpen && user ? (
        <AvatarMenuActionSheet
          navigateToProfile={() => onSideNavPress(APP_ROUTES.Account)}
          onClose={() => setAvatarMenuSheetOpen(false)}
          popoverOffset={16}
          targetRef={menuSectionRef}
        />
      ) : null}
      {isMoreMenuSheetOpen ? (
        <MoreMenuActionSheet
          onClose={() => setIsMoreMenuSheetOpen(false)}
          popoverOffset={16}
          sections={moreMenuSections}
          targetRef={moreMenuActionSheetRef}
        />
      ) : null}
      {isRedirectSheetOpen && selectedStaticLinkURL ? (
        <RedirectActionSheet
          isRedirectSheetOpen={isRedirectSheetOpen}
          redirectURL={selectedStaticLinkURL}
          setIsRedirectSheetOpen={setIsRedirectSheetOpen}
        />
      ) : null}
    </View>
  );
};

/*
 * react-navigation's DrawerItem hardcodes a 32px right margin on its label
 * container plus 8px of wrapper padding, and neither is overridable for
 * function labels. We pull the item's right edge back with a negative margin
 * so the item box still bleeds to the drawer edge (clipped by overflow:hidden,
 * keeping the active highlight full-width) while leaving content a clean 16px
 * right gap: 32 (label) + 8 (wrapper) - 24 (this offset) = 16px. This gap
 * applies to every item's label and to the More tile's right-aligned chevron.
 * https://github.com/react-navigation/react-navigation/blob/main/packages/drawer/src/views/DrawerItem.tsx#L221
 */
const LABEL_OFFSET_MARGIN = -24;

const useStyles = createStyles((theme) => ({
  avatarContainer: {
    alignItems: 'center',
    marginBottom: 0,
    paddingVertical: 16,
  },
  bottomList: {
    marginBottom: 8,
  },
  closeButtonContent: {
    justifyContent: 'flex-start',
  },
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  contentHeader: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  contentHeaderTablet: {
    paddingTop: 16,
  },
  disabled: {
    opacity: 0.5,
  },
  drawerItem: {
    borderRadius: 0,
    marginLeft: 0,
    marginRight: LABEL_OFFSET_MARGIN,
    marginVertical: 0,
  },
  drawerItemMedium: {
    // 16 here + react-navigation's 8px wrapper padding = a 24px left gap for
    // item content, measured the same way as the 16px right gap.
    paddingLeft: 16,
  },
  drawerNavItems: {
    // Set flexGrow so the item list scrolls instead of overflowing
    flexGrow: 1,
  },
  drawerNavItemsTablet: {
    paddingTop: 16,
  },
  drawerScrollContainer: {
    // Set flexGrow so the item list scrolls instead of overflowing
    flexGrow: 1,
  },
  drawerScrollContainerLarge: {
    backgroundColor: theme.colors.accent,
  },
  drawerScrollContainerMedium: {
    backgroundColor: theme.colors.background,
  },
  headerItem: {
    flex: 1,
  },
  logoImageTablet: {
    height: 36,
  },
  moreLabelRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    // DrawerItem's label container already fills the available width. Adding
    // flex here makes this nested row grow on its parent's vertical axis; on
    // native Yoga that parent has no explicit height, so the text collapses.
    // No extra right padding: LABEL_OFFSET_MARGIN already lands content (text
    // and this row's right-aligned chevron) 16px from the drawer edge.
  },
  paddingButton: {
    borderWidth: 0,
    // Override Button's own paddingHorizontal (16px at medium size) with the
    // same shorthand — a paddingLeft override loses to the shorthand on
    // react-native-web. The icon is left-aligned, so the right side is moot.
    paddingHorizontal: SCREEN_PADDING,
  },
  supportButton: {
    alignItems: 'center',
  },
}));
